import type {AgencyInviteSerializer} from "@/generated/User";
import {useGenericInfiniteQuery, useGenericQuery} from "@/hooks/useQueryHooks";
import {client} from "@/httpClient";
import type {TCreateAgencyFormState} from "@/store/createAgency";
import type {TAccountMember, TAgency, TInfiniteQueryOptions, TQueryOptions, TSingleBrand} from "@/store/types";
import {handleErrorWithToast} from "@/utils/errorHandlers";
import {useMutation, type UseMutationOptions} from "@tanstack/react-query";
import type {AxiosError} from "axios";

export const AGENCY_KEYS = {
  mAgency: (agencyId: number, params = "") => [`/agency/${agencyId}/${params ? `?${params}` : ""}`],
  mAgencyBrandsMerge: ["/agency/merge_brands/"],
  mAgencyInvite: (agencyId: number) => [`/agency/${agencyId}/invite_user/`],
  mAgencyMemberRegistration: (agencyId: number) => [`/agency/${agencyId}/register_agency_user/`],
  mAgencyPreRegister: ["/agency/pre_register/"],
  mAgencyRegister: ["/agency/register/"],
  mAgencySubBrandRegister: (agencyId: number) => [`/agency/${agencyId}/add_sub_brand/`],
  qAgency: (agencyId: number) => [`/agency/${agencyId}/`],
  qAgencyCampaigns: (params?: {agencyId?: number; status: "active" | "open" | "complete" | "paused" | "drafts"}) => [
    `/agency/${params?.agencyId}/${params?.status ?? "all"}/`,
  ],
  qAgencyFavouriteContent: (agencyId: number, params: string) => [
    `/agency/favourite_content/?agency_id=${agencyId}${params}`,
  ],
  qAgencyFavouriteContentStats: (agencyId: number) => [`/agency/favourite_content_stats/?agency_id=${agencyId}`],
  qAgencyInvites: (agencyId: number) => [`/agency/${agencyId}/invites/`],
  qAgencyList: (params: string) => [`/agency/?${params}`],
  qAgencyMergeBrandsPreview: (selectedCompanyIds: number[]) => [
    `/agency/merge_brands_preview/?company_profile_ids=${selectedCompanyIds.join(",")}`,
  ],
  qAgencyMembers: (agencyId: number) => [`/agency/${agencyId}/members/`],
  qAgencyPreRegisterDetails: (uuid: string) => [`/agency/pre_register_details/?uuid=${uuid}`],
  qAgencySubbrands: (agencyId: number) => [`/agency/${agencyId}/sub_brands/`],
} as const;

export const useAgencyInviteMutation = ({
  agencyId,
  email,
  mutationOptions,
}: {
  agencyId: number;
  email: string;
  mutationOptions?: Partial<UseMutationOptions>;
}) =>
  useMutation({
    mutationKey: AGENCY_KEYS.mAgencyInvite(agencyId),
    mutationFn: () => client.post(AGENCY_KEYS.mAgencyInvite(agencyId)[0], {emails_data: email}),
    onError: (error: AxiosError<{error: string}>) => handleErrorWithToast(error),
    ...mutationOptions,
  });

export const useAgencyQuery = ({agencyId, queryOptions}: {agencyId: number; queryOptions?: TQueryOptions<TAgency>}) =>
  useGenericQuery<TAgency>({
    queryKey: AGENCY_KEYS.qAgency(agencyId),
    enabled: !!agencyId,
    ...queryOptions,
  });

export const useAgencyInvitesQuery = ({
  agencyId,
  queryOptions,
}: {
  agencyId: number;
  queryOptions?: TQueryOptions<AgencyInviteSerializer[]>;
}) =>
  useGenericQuery<AgencyInviteSerializer[]>({
    queryKey: AGENCY_KEYS.qAgencyInvites(agencyId),
    enabled: !!agencyId,
    ...queryOptions,
  });

export const useAgencyMembersQuery = ({
  agencyId,
  queryOptions,
}: {
  agencyId: number;
  queryOptions?: TInfiniteQueryOptions<TAccountMember>;
}) =>
  useGenericInfiniteQuery<TAccountMember>({
    queryKey: AGENCY_KEYS.qAgencyMembers(agencyId),
    enabled: !!agencyId,
    ...queryOptions,
  });

export const useAgencyPreRegisterDetailsQuery = ({uuid}: {uuid: string}) =>
  useGenericQuery<TCreateAgencyFormState>({
    queryKey: AGENCY_KEYS.qAgencyPreRegisterDetails(uuid),
    enabled: !!uuid,
  });

export const useAgencySubbrandsQuery = ({
  agencyId,
  queryOptions,
}: {
  agencyId: number;
  queryOptions?: TQueryOptions<TSingleBrand[]>;
}) =>
  useGenericQuery<TSingleBrand[]>({
    queryKey: AGENCY_KEYS.qAgencySubbrands(agencyId),
    enabled: !!agencyId,
    ...queryOptions,
  });
